import type { PlanRow } from '../models/useBilling'
import type { ComputeClass, ProvisionedTask } from '../models/useTasks'
import { capitalize } from './general.ts'

/**
 * The provisioned-task list's display rules (the app half of router#968's deadline).
 *
 * A provisioned task is one ECS container. `deadline_at` is the instant its compute stops on its
 * own; the container and everything on its own disk go then, while the node keeps its name,
 * labels, grants and node storage, so `provision_node` on the same name brings it back. Kept out
 * of the component because every rule here has a wrong answer that looks plausible.
 */

/**
 * How long a finished task stays on the list.
 *
 * A stopped task is the thing a reader is looking for when they ask where their machine went, and
 * the router records no other trace of the stop: the compute-stop sweeper is not a tool call, so
 * it writes NO `audit_log` row (router#968). This list is therefore the only place the ending is
 * visible at all, which is why the window is days rather than EC2's hour.
 */
export const ENDED_WINDOW_DAYS = 7

/**
 * `running` — the container is up.
 * `expiring` — past its deadline and not yet stopped. The sweeper ticks every 10 minutes and
 *   bounds its stops per tick, so this state is normal for a few minutes and is NOT an error.
 * `ended` — `completed_at` is stamped. The container and its disk are gone.
 */
export type TaskState = 'running' | 'expiring' | 'ended'

export type TaskLifecycle = {
  state: TaskState
  /**
   * ⚠⚠ NULL MEANS NO DEADLINE, NOT "EXPIRED" AND NOT "NOW". Every task created before migration
   * 0152 has none, and `max_hours: 0` on a paid plan still mints one — such a task was promised it
   * runs until `deprovision_node`, and the sweeper skips it. Rendering an empty or full bar for
   * these says the opposite of what is true.
   */
  deadlineAt: Date | null
  /**
   * How far the task is from `created_at` to `deadline_at`, clamped to 0..1. Null when there is no
   * deadline — there is no fraction of an unbounded interval.
   */
  fraction: number | null
  /** Milliseconds left until the deadline; NEGATIVE once past it. Null when there is no deadline. */
  remainingMs: number | null
  endedAt: Date | null
  /**
   * Whether this task ran to its deadline rather than being stopped before it.
   *
   * ⚠ IT IS A STATEMENT ABOUT TWO TIMESTAMPS AND NOT ABOUT A CAUSE. Nothing durable records WHY a
   * task stopped — the deadline sweeper writes no audit row and stamps the same `completed_at` a
   * `deprovision_node` does — so the copy this feeds has to describe the timestamps ("ran to its
   * deadline") and must never assert who ended it.
   */
  endedAtOrAfterDeadline: boolean
}

/**
 * The lifecycle of one task as of `now`.
 *
 * ⚠⚠ AN ENDED TASK IS MEASURED AT `completed_at`, NEVER AT `now`. Its progress is a fact that
 * stopped moving when the container did; carrying `now` into the arithmetic would walk a finished
 * task's bar on past 100% for as long as the row stays on the list, and print a "remaining" for a
 * machine that no longer exists.
 */
export const taskLifecycle = (task: ProvisionedTask, now: Date): TaskLifecycle => {
  const endedAt = task.completedAt ? new Date(task.completedAt) : null
  const deadlineAt = task.deadlineAt ? new Date(task.deadlineAt) : null
  const at = endedAt ?? now

  if (!deadlineAt) {
    return {
      state: endedAt ? 'ended' : 'running',
      deadlineAt: null,
      fraction: null,
      remainingMs: null,
      endedAt,
      endedAtOrAfterDeadline: false,
    }
  }

  const start = new Date(task.createdAt).getTime()
  const span = deadlineAt.getTime() - start
  const elapsed = at.getTime() - start
  // ⚠ A non-positive span is not a task that is 100% done — it is a row whose two instants cannot
  // describe an interval, which `max_hours` cannot produce but a clock skew or a hand-written row
  // can. Reporting null keeps the bar off rather than pinning it full.
  const fraction = span > 0 ? Math.min(1, Math.max(0, elapsed / span)) : null
  const remainingMs = deadlineAt.getTime() - at.getTime()

  return {
    state: endedAt ? 'ended' : remainingMs <= 0 ? 'expiring' : 'running',
    deadlineAt,
    fraction,
    remainingMs,
    endedAt,
    endedAtOrAfterDeadline: endedAt !== null && remainingMs <= 0,
  }
}

/**
 * A coarse duration, largest two units. Used for both "left" and "ago", so it carries no direction
 * of its own and takes a magnitude — the caller says which side of the deadline it is on.
 */
export const formatDuration = (ms: number): string => {
  const total = Math.max(0, Math.floor(Math.abs(ms) / 1000))
  const days = Math.floor(total / 86400)
  const hours = Math.floor((total % 86400) / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`
  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
  if (minutes > 0) return `${minutes}m`
  return 'under a minute'
}

/**
 * The shape a task ran on.
 *
 * ⚠⚠ AN OFF-CATALOGUE SHAPE MUST NOT BE SNAPPED TO THE NEAREST CLASS — the same rule
 * `computeClassLabel` applies to the usage breakdown, and for the same reason. `provision_node`
 * took a caller-supplied cpu/memory pair before router#927 and clamped it to powers of two, so
 * rows exist whose shape matches no class; `docs/PRICING.md` § 4.2 requires they be named by what
 * actually ran. The catalogue is matched on the EXACT pair because `compute_classes` is unique on
 * `(cpu, memory_mb)` — that uniqueness is what lets billing derive the class back from the shape.
 *
 * ⚠ A retired class still names a shape. `offered` is a soft retire precisely so a task that
 * already ran keeps resolving, so this must not filter on it.
 */
export const taskShapeLabel = (task: ProvisionedTask, classes: ComputeClass[]): string => {
  // ⚠ Pre-2.13 task rows carry no shape at all. "unknown" is the honest answer; a default pair
  // would name a machine size we have no record of running.
  if (task.cpu === null || task.memoryMb === null) return 'unknown size'
  const match = classes.find(c => c.cpu === task.cpu && c.memoryMb === task.memoryMb)
  return match ? match.name : `${task.cpu} CPU units · ${task.memoryMb} MB`
}

/**
 * Running machines first, soonest deadline first; then the ended ones, most recently ended first.
 *
 * ⚠ A running task with NO deadline sorts after every dated one rather than before them. It is the
 * one that will still be there tomorrow, so it is the least urgent thing on the screen — sorting a
 * null as 0 would put the only machine nobody has to act on at the top of the list.
 */
export const compareTasks = (a: TaskLifecycle, b: TaskLifecycle): number => {
  const ended = (l: TaskLifecycle) => (l.state === 'ended' ? 1 : 0)
  if (ended(a) !== ended(b)) return ended(a) - ended(b)

  if (a.state === 'ended' && b.state === 'ended') {
    return (b.endedAt?.getTime() ?? 0) - (a.endedAt?.getTime() ?? 0)
  }

  const due = (l: TaskLifecycle) => l.deadlineAt?.getTime() ?? Number.POSITIVE_INFINITY
  return due(a) - due(b)
}

/** How full the bar is allowed to look before it changes colour. */
export const deadlineSeverity = (lifecycle: TaskLifecycle): 'primary' | 'warning' | 'error' => {
  if (lifecycle.state === 'expiring') return 'error'
  const fraction = lifecycle.fraction ?? 0
  if (fraction >= 0.9) return 'error'
  if (fraction >= 0.75) return 'warning'
  return 'primary'
}

/**
 * The node page this machine opens, or null when there is none to open.
 *
 * ⚠⚠ THREE REASONS THERE IS NO PAGE, AND `nodeDeleted` IS ONLY ONE OF THEM. The container has not
 * registered yet, so there is no node id at all; the node is soft-deleted, the ordinary state of a
 * terminated task, and its page would 404; or the node row did not come back for this reader at all.
 * ⚠ Its network does not decide: `/nodes/:nodeId` draws a node of another org's network or of no
 * network (app#388).
 *
 * ⚠⚠ THAT LAST CASE IS WHY THIS IS NOT `task.nodeId && !task.nodeDeleted`. `nodeDeleted` is read
 * off a row that never arrived, so it is FALSE — which reads as "live" and earns a link to a page
 * that cannot load, under the frozen provision-time name.
 */
export const nodePageHref = (task: ProvisionedTask): string | null =>
  task.nodeId !== null && task.nodeReadable && !task.nodeDeleted
    ? `/nodes/${task.nodeId}`
    : null

/**
 * How a row reads in the Status column — the vocabulary EC2 and Docker Desktop
 * use for the same list, because that is the list a reader has seen before.
 *
 * ⚠⚠ `starting` IS A REAL STATE AND NOT A LOADING SPINNER. Between `provision_node`
 * returning and the container's agent registering — about a minute — the task
 * exists, bills, and has no node: no id, no page to link to, and the only name
 * anyone has is the one the provision was asked for. A row that read `Running`
 * there would offer a link to a node that does not exist yet.
 *
 * ⚠ `stopping` is the machine past its deadline and not yet swept. The sweeper
 * ticks every ten minutes and bounds its stops per tick, so this is NORMAL for a
 * few minutes and is not an error — it is `expiring` with a word a reader of
 * this list already knows.
 */
export type TaskStatus = 'starting' | 'running' | 'stopping' | 'terminated'

export const taskStatus = (task: ProvisionedTask, lifecycle: TaskLifecycle): TaskStatus => {
  // ⚠ ENDED WINS OVER EVERY OTHER SIGNAL. A terminated task has no node id either
  // — it was soft-deleted with the container — so asking "has it registered?"
  // first would report a finished machine as `starting` forever.
  if (lifecycle.state === 'ended') return 'terminated'
  if (task.nodeId === null) return 'starting'
  return lifecycle.state === 'expiring' ? 'stopping' : 'running'
}

/** The words and the colour, in one place so the table and any later surface agree. */
export const taskStatusLabel: Record<TaskStatus, string> = {
  starting: 'Starting',
  running: 'Running',
  stopping: 'Stopping',
  terminated: 'Terminated',
}

export const taskStatusColor: Record<TaskStatus, 'default' | 'success' | 'warning' | 'error'> = {
  // ⚠ Not `success`: it is not up yet, and a green chip beside a name with no
  // page behind it is the one combination that reads as a broken link.
  starting: 'default',
  running: 'success',
  stopping: 'error',
  // ⚠ Terminated is NEUTRAL and never `error`. Reaching a deadline, or being
  // stopped on purpose, is the ordinary end of a machine's life — colouring it
  // as a failure makes the list look like a list of problems.
  terminated: 'default',
}

/**
 * What a caller may pick when moving a machine's deadline.
 *
 * ⚠⚠ EVERY PRESET IS AN INSTANT FROM NOW, NOT AN AMOUNT ADDED, because that is
 * exactly what the router's `max_hours` means: "how many hours FROM NOW this
 * task may run", never "how many more". A menu labelled "+4 hours" over that API
 * is a lie on any task with more than four hours left — picking it would CUT the
 * machine's life, and the call would report success. So the labels say `In 4
 * hours`, each carries the resulting instant for the screen to print, and the
 * one reading that would SHORTEN the deadline is marked rather than hidden.
 *
 * ⚠ The app does NOT compute a delta from the remaining time to turn these into
 * genuine additions. It could — but `deadline_at` is the router's to resolve
 * (its own field description says to read it and not recompute it), the app's
 * copy is up to one poll stale, and an arithmetic layer here is a second answer
 * to a question the router already answers exactly.
 *
 * ⚠⚠ "No deadline" IS OFFERED ONLY WHERE THE PLAN ALLOWS IT, and it is the one
 * choice that removes a protection rather than moving it. `max_hours: 0` is
 * refused on Free — the deadline and the included hours are its only two bounds —
 * and accepted on every other plan, including the exempt Enterprise tier from
 * router#1216. The flag comes from the plan row (`compute_deadline_optional`), so
 * the app is reading the same column the router decides on rather than keeping a
 * second list of which plans may.
 *
 * ⚠ Offering it to a plan that cannot have it would be a control whose only
 * outcome is a refusal, on the tier 268 of our 270 orgs are on.
 */
export type DeadlinePreset = {
  /** Hours from now, sent as `max_hours`. `0` is No deadline. */
  hours: number
  label: string
  /** The instant this preset produces. Null only for No deadline, which has none. */
  at: Date | null
  /** True when this preset lands EARLIER than the deadline the task holds now. */
  shortens: boolean
  /** Shown but not selectable: the plan does not allow it. `hint` says where it is. */
  disabled?: boolean
  hint?: string
}

/** The cheapest offered plan that passes `allows`, to name it on a greyed entry. */
const cheapestPlan = (plans: readonly PlanRow[], allows: (p: PlanRow) => boolean): PlanRow | null =>
  [...plans]
    .filter(p => allows(p) && p.offered !== false)
    .sort((a, b) => a.monthlyFeeCents - b.monthlyFeeCents || a.name.localeCompare(b.name))[0] ?? null

const availableOn = (plan: PlanRow | null): string | undefined =>
  plan ? `Available on ${capitalize(plan.name)}` : undefined

/**
 * The cheapest offered plan that allows No deadline, to name it on a greyed entry.
 * Null when the catalogue has none (or was not read).
 */
export const noDeadlinePlan = (plans: readonly PlanRow[]): PlanRow | null =>
  cheapestPlan(plans, p => p.computeDeadlineOptional === true)

/** The plan's longest deadline in hours (`compute_max_hours_limit`), or null for no maximum. */
export const planMaxHours = (plan: Pick<PlanRow, 'computeMaxHoursLimit'> | null | undefined): number | null => {
  const hours = plan?.computeMaxHoursLimit
  return typeof hours === 'number' && hours > 0 ? hours : null
}

/**
 * The greyed No deadline entry's hint, or undefined when no catalogue row names a plan.
 * ⚠ Never a literal plan name: it goes wrong when the plans change.
 */
export const noDeadlineHint = (plans: readonly PlanRow[]): string | undefined => availableOn(noDeadlinePlan(plans))

/**
 * The greyed state of a choice above the plan's maximum (router#1549). The router refuses it, so
 * it is shown — a Free reader sees what a paid plan adds — and cannot be picked. The hint names
 * the cheapest offered plan whose maximum allows it. Empty when the choice is allowed.
 */
export const aboveMaximum = (
  hours: number,
  maxHours: number | null,
  plans: readonly PlanRow[],
): { disabled?: true; hint?: string } => {
  if (maxHours === null || hours <= maxHours) return {}
  const hint = availableOn(cheapestPlan(plans, p => (planMaxHours(p) ?? Infinity) >= hours))
  return { disabled: true, ...(hint ? { hint } : {}) }
}

const PRESET_HOURS = [4, 12, 24, 24 * 3, 24 * 7] as const

/** The plan's default deadline in hours, or null when it has none or is not loaded. */
export const planDefaultHours = (plan: Pick<PlanRow, 'computeMaxHoursDefault'> | null | undefined): number | null => {
  const hours = plan?.computeMaxHoursDefault
  return typeof hours === 'number' && hours > 0 ? hours : null
}

/**
 * The hours a deadline menu offers, ascending. The plan's default is ADDED when it is not one of
 * the fixed choices, so the "(default)" entry is always there to mark.
 */
export const menuHours = (defaultHours: number | null): number[] =>
  [...new Set<number>([...PRESET_HOURS, ...(defaultHours ? [defaultHours] : [])])].sort((a, b) => a - b)

/** ` (default)` on the plan's default entry, nothing on the others. */
export const defaultSuffix = (hours: number, defaultHours: number | null): string =>
  hours === defaultHours ? ' (default)' : ''

/** `In 4 hours` / `In 3 days` — the same coarse units the rest of the table uses. */
const presetLabel = (hours: number): string =>
  hours % 24 === 0 && hours >= 24
    ? `In ${hours / 24} day${hours === 24 ? '' : 's'}`
    : `In ${hours} hours`

export const deadlinePresets = (
  lifecycle: TaskLifecycle,
  now: Date,
  allowUnlimited: boolean,
  plans: readonly PlanRow[] = [],
  defaultHours: number | null = null,
  maxHours: number | null = null,
): DeadlinePreset[] => {
  const presets: DeadlinePreset[] = menuHours(defaultHours).map(hours => {
    const at = new Date(now.getTime() + hours * 3600_000)
    return {
      hours,
      label: presetLabel(hours) + defaultSuffix(hours, defaultHours),
      at,
      // ⚠ A task with NO deadline cannot be shortened by any of these — it has
      // no instant to move earlier — but every one of them ADDS a stop where
      // there was none, which is a different thing and the screen says so.
      shortens: lifecycle.deadlineAt !== null && at < lifecycle.deadlineAt,
      ...aboveMaximum(hours, maxHours, plans),
    }
  })
  return [
    // ⚠ No "My plan's default" entry (Benoit, 2026-09-15): the plan's default hours are marked
    // "(default)" in the list instead, so every entry states its instant.
    ...presets,
    // ⚠⚠ `0` IS "NO DEADLINE" AND NEVER "STOP NOW", which is the one way this
    // value could be misread — by a reader of the menu and by a future author of
    // it. It is last, it is the only entry that removes rather than moves, and
    // the screen confirms it separately.
    //
    // ⚠ It carries no instant because there is no instant: the machine runs
    // until somebody terminates it. `shortens` is false for the same reason —
    // the furthest thing from an earlier stop that a change can be.
    //
    // ⚠ Always listed, so a Free reader sees what a paid plan adds (Benoit, 2026-09-15). Where the
    // plan does not allow it the entry is DISABLED: the router refuses 0 there.
    allowUnlimited
      ? { hours: 0, label: 'No time limit', at: null, shortens: false }
      : { hours: 0, label: 'No time limit', at: null, shortens: false, disabled: true, hint: noDeadlineHint(plans) },
  ]
}

/** Whether a preset REMOVES the deadline rather than moving it — the one that needs confirming. */
export const removesDeadline = (preset: DeadlinePreset): boolean => preset.hours === 0
